import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import KeepAlive, { useActivate, useUnactivate, useAliveController } from 'react-activation';

import { logging, logPostInfo } from '../../actions/log';
import { getCategoryImgInfo, getCategoryLabels } from '../../actions/wallpaper';
import Loading from '../../components/Loading/Loading';
import WallpaperCard from '../../components/WallpaperCard/NS_WallpaperCard';
import { getQueryString } from '../../utils/utils';
import Blank from './Blank';
import Nav from './Nav';
import styles from './WallpaperCategory.module.less';
import WallpaperCategoryNav from './WallpaperCategoryNav';
import InfiniteScroll from '../../components/InfinteScroll/AutoHeightGridInfinteScroll';
import DefaultImage from '../../static/wp_default.svg';

const selectWpCategoryLabels = createSelector(
  state => {
    return state.wallpaper && state.wallpaper;
  },
  info => info.labelList || [],
);

const selectWpCategoryImgInfo = createSelector(
  state => state.wallpaper && state.wallpaper,
  info => {
    return info.categoryInfo;
  },
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

function WPCategory() {
  const [load, setLoad] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const { action, search } = history;
  const [activeClass, setActiveClass] = useState('-1');
  const [activeTag, setActiveTag] = useState('-1');
  const [columnMap, setColumnMap] = useState({});
  const columnWidth = [250, 385, 250];

  const classList = useSelector(selectWpCategoryLabels);
  const [tagList, setTagList] = useState([]);
  const categoryImgInfo = useSelector(selectWpCategoryImgInfo);
  const logInfoData = useSelector(selectLogInfo);

  const [initTime, setInitTime] = useState(+new Date());

  // 浏览时长打桩
  const leaveTimeLog = useCallback(() => {
    const leaveTime = +new Date();
    dispatch(
      logging({
        category: 'I',
        action: 'View_Time',
        label: '',
        param2: {
          source: `Wallpaper_Category||${activeClass}||${activeTag}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          initTime,
          leaveTime,
          time: leaveTime - initTime,
        },
      }),
    );
  }, [initTime, activeClass, activeTag]);

  const pagePvLog = useCallback(() => {
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'P_Wallpaper_Category',
        label: '',
        param2: {
          source: 'Wallpaper_Category',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
    dispatch(
      logPostInfo({
        source: 'Wallpaper_Reco',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  });

  useActivate(() => {
    setInitTime(+new Date());
  });
  useUnactivate(() => {
    leaveTimeLog();
  });

  useEffect(() => {
    pagePvLog();
  }, []);
  useActivate(() => {
    pagePvLog();
  });

  const getScrollList = useCallback(pagenation => {
    dispatch(
      getCategoryImgInfo({
        ...categoryImgInfo.pagenation,
        ...pagenation,
      }),
    );
  });

  useEffect(() => {
    getScrollList({
      tagId: activeTag,
      classId: activeClass,
      skip: 0,
      refresh: true,
    });
  }, [activeClass, activeTag]);
  useEffect(() => {
    dispatch(getCategoryLabels());
  }, []);

  const handleClass = useCallback(
    classifyId => () => {
      if (classifyId !== activeClass) {
        setActiveClass(classifyId);
        const tagLists = classList.find(item => item.classifyId === classifyId).tags || [];
        setTagList(tagLists);
        setActiveTag(tagLists[0] && tagLists[0].tagId);
        dispatch(
          logging({
            category: 'Wallpaper',
            action: 'C_Wallpaper_Category_Tag',
            label: '',
            param2: {
              source: `Wallpaper_Category||${classifyId}||${tagLists[0] && tagLists[0].tagId}`,
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
            },
          }),
        );
      }
    },
    [activeClass, classList],
  );
  useEffect(() => {
    if (classList[0] && (action === 'PUSH' || tagList.length === 0)) {
      const classId = getQueryString('classId', search) || '-1';
      const tagId = getQueryString('tagId', search) || '-1';
      setActiveClass(classId);
      setActiveTag(tagId);
      const tags = classList.find(item => item.classifyId === classId) || {};
      setTagList(tags.tags || []);
    }
  }, [classList]);

  const handleTag = useCallback(
    tagId => () => {
      if (activeTag !== tagId) {
        setActiveTag(tagId);
        dispatch(
          logging({
            category: 'Wallpaper',
            action: 'C_Wallpaper_Category_Tag',
            label: '',
            param2: {
              source: `Wallpaper_Category||${activeClass}||${tagId}}`,
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
            },
          }),
        );
      }
    },
    [tagList, activeTag, activeClass],
  );

  useEffect(() => {
    if (load === null) {
      setLoad(true);
    } else {
      setLoad(false);
    }
  }, [categoryImgInfo]);

  const loadMore = useCallback(() => {
    if (!categoryImgInfo.load && categoryImgInfo.list.length < categoryImgInfo.pagenation.count) {
      const params = {
        ...categoryImgInfo.pagenation,
      };
      delete params.count;
      dispatch(
        getCategoryImgInfo({
          ...params,
          classId: activeClass,
          tagId: activeTag,
          skip: categoryImgInfo.list.length,
          refresh: false,
        }),
      );
    }
  }, [categoryImgInfo, activeClass, activeTag]);

  const Header = () => {
    return (
      <>
        <div className={styles.label}>
          <WallpaperCategoryNav
            list={classList}
            handleCategory={handleClass}
            active={activeClass}
            text="分类"
            keyId="classifyId"
            keyName="classifyName"
            keyWord="class"
          />
          <WallpaperCategoryNav
            list={tagList}
            handleCategory={handleTag}
            active={activeTag}
            text="标签"
            keyId="tagId"
            keyName="tagName"
            keyWord="tag"
          />
        </div>
      </>
    );
  };
  const DefaultCard = () => {
    return <div className={styles.default_img} />;
  };

  if ((load || load === null) && action !== 'POP') {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }
  return (
    <div className={styles.page}>
      <div className={styles.nav}>
        <div className={styles.nav_container}>
          <Nav active="wpcategory" />
        </div>
      </div>
      <div className={styles.page_container}>
        <InfiniteScroll
          dataSource={categoryImgInfo.list || []} // 数据集
          count={categoryImgInfo.pagenation.count}
          gap={10}
          columnWidth={columnWidth}
          getItemSize={index => {
            const commonHeight = (columnMap[columnWidth[index % 3]] || 190) + 15;
            const defaultHeaderHeight =
              (Math.ceil(classList.length / 9) + Math.ceil(tagList.length / 9)) * 32 + 14 + 6;
            // eslint-disable-next-line radix
            let tpl = +parseInt((columnMap && columnMap.tpl) || 0);
            if (tpl < 84) {
              tpl = defaultHeaderHeight;
            }
            const temp = index === 0 ? commonHeight + tpl : commonHeight;
            return temp;
          }} // 行高
          loadData={loadMore}
          getHeight={param => setColumnMap(param)}
          template={(item, index) => {
            return (
              <WallpaperCard
                wallpaper={item}
                key={index}
                cardInfo={{
                  pageSource: 'category',
                  classId: activeClass,
                  tagId: activeTag,
                  isCollect: true,
                  isWallpaper: true,
                  isDeleteColl: false,
                  isDeleteUsed: false,
                  isPreview: true,
                }}
                logInfo={{
                  source: `Wallpaper_Category||${activeClass}||${activeTag}`,
                }}
              />
            );
          }}
          tpl={Header}
          resizeTemplate={DefaultCard}
          blank={() => <Blank load={categoryImgInfo.load} more />}
        />
      </div>
      {/* {categoryImgInfo.pagenation.count < 1 && <Blank load={categoryImgInfo.load} more />} */}
    </div>
  );
}

export default () => {
  const history = useHistory();
  const { refresh } = useAliveController();

  useEffect(() => {
    if (history.action === 'PUSH') {
      refresh(`wpcateg`);
    }
  }, []);
  return (
    <KeepAlive name="wpcateg">
      <WPCategory />
    </KeepAlive>
  );
};
